import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from './org/OrgProvider';
import { useAppSwitcherContext } from './AppSwitcherProvider';
import { StyledButton } from './shared/StyledButton';
import { useLanguage } from '../i18n/LanguageProvider';
import { Z_INDEX } from '../constants/zIndex';

const styles = {
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: '0',
  },
  modal: {
    position: 'fixed' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#ffffff',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '500px',
    width: '90%',
  },
  overlay: {
    zIndex: Z_INDEX.upgradeModal,
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
};

type ContextProps = {
  showUpgradeModal: (feature: string) => void;
  hideUpgradeModal: () => void;
};

const defaultContext: ContextProps = {
  showUpgradeModal: (feature: string) => {},
  hideUpgradeModal: () => {},
};

const SubscriptionModalContext = createContext(defaultContext);

export const SubscriptionModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { messages } = useLanguage();
  const { app } = useAppSwitcherContext();
  const { org } = useOrgContext();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState('');

  const showUpgradeModal = useCallback((featureName: string) => {
    setFeature(featureName);
    setIsOpen(true);
  }, []);

  const hideUpgradeModal = useCallback(() => {
    setIsOpen(false);
    setFeature('');
  }, []);

  const handleViewPlans = useCallback(() => {
    if (!org) {
      navigate('/checkout');
      hideUpgradeModal();
      return;
    }

    const targetApp = app || 'clientell';
    navigate(`/${targetApp}/orgs/${org.id}/checkout`);
    hideUpgradeModal();
  }, [app, org, navigate, hideUpgradeModal]);

  return (
    <SubscriptionModalContext.Provider
      value={{ showUpgradeModal, hideUpgradeModal }}
    >
      {children}
      {isOpen ? (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.title}>{messages.common.upgradeRequired}</h3>
            <p>{messages.common.upgradeToAccess}</p>
            <p style={{ fontWeight: 'bold' }}>{feature}</p>
            <div style={styles.modalActions}>
              <StyledButton
                variant="secondary"
                size="medium"
                onClick={hideUpgradeModal}
              >
                {messages.common.gotIt}
              </StyledButton>
              <StyledButton
                variant="error"
                size="medium"
                onClick={handleViewPlans}
              >
                {messages.common.viewPlans}
              </StyledButton>
            </div>
          </div>
        </div>
      ) : null}
    </SubscriptionModalContext.Provider>
  );
};

export const useSubscriptionModal = () => useContext(SubscriptionModalContext);
